// Common formatting utilities for reports

import { CURRENCY_SYMBOL } from '@/constants';

export const formatDate = (d: Date | null) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const formatText = (v: unknown) =>
  v === null || v === undefined || v === '' ? '-' : String(v);

/* export const formatNumber = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : '-';
}; */

export const formatNumber = (v: unknown, decimals: number = 0) => {
  const n = Number(v);

  // If value is invalid, use default 0
  const safeNumber = Number.isFinite(n) ? n : 0;

  const formatted = safeNumber.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  // Add currency symbol only if number > 0
  return safeNumber > 0 ? `${CURRENCY_SYMBOL}${formatted}` : formatted;
};

export const formatRawNumber = (v: unknown) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : '—';
};

export const formatCurrency = (v: unknown, decimals: number = 0) => {
  const n = Number(v);

  // Use default 0 if invalid
  const safeNumber = Number.isFinite(n) ? n : 0;

  const formatted = safeNumber.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  // Add currency symbol only if number > 0
  return safeNumber > 0 ? `${CURRENCY_SYMBOL}${formatted}` : formatted;
};

export const formatPercent = (v: unknown) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const decimals = (n.toString().split('.')[1] || '').length;
  // If more than 2 decimals, limit to 2; otherwise, keep original
  return decimals > 2 ? `${n.toFixed(2)}%` : `${n}%`;
};

export const formatNet = (
  gross: unknown,
  tax: unknown,
  decimals: number = 0
) => {
  const g = Number(gross);
  const t = Number(tax);

  // Use default 0 if invalid
  const safeGross = Number.isFinite(g) ? g : 0;
  const safeTax = Number.isFinite(t) ? t : 0;

  const net = Math.round(safeGross) - Math.round(safeTax);

  const formatted = net.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  // Add currency symbol only if net > 0
  return net > 0 ? `${CURRENCY_SYMBOL}${formatted}` : formatted;
};

export const formatFullDate = (iso?: string | null) => {
  if (!iso) return '—';

  // Create date from YYYY-MM-DD parts to avoid timezone conversion
  let d: Date;
  try {
    const datePart = iso.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } catch {
    d = new Date(iso);
  }

  if (isNaN(d.getTime())) return '—';
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const month = d.toLocaleDateString(undefined, { month: 'long' });
  const day = d.getDate();
  const getOrdinal = (dd: number) => {
    const j = dd % 10,
      k = dd % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };
  return `${weekday}, ${month} ${day}${getOrdinal(day)}`;
};
